/**
 * Utility helpers for formatting, dates, and estimate math.
 */

export function formatMoney(amount: number | string | null | undefined): string {
  if (amount == null || amount === '') return '—';
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  return '$' + n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const date = new Date(dateStr + 'T00:00:00');
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * "August 22nd" — the long, written-out form the contractor documents use for
 * pickup and delivery windows. A crew reading a date on a phone at 7am should
 * not have to decode 2026-08-22, and an ambiguous 08/09 is a wrong-day risk.
 */
export function formatDateLong(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const date = new Date(String(dateStr) + 'T00:00:00');
  if (isNaN(date.getTime())) return '';
  const day = date.getDate();
  const tens = day % 100;
  const suffix = tens >= 11 && tens <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][day % 10] || 'th';
  return `${date.toLocaleDateString('en-CA', { month: 'long' })} ${day}${suffix}`;
}

/** "August 22nd – August 24th", collapsing to a single date when they match. */
export function formatDateRangeLong(start: string | null | undefined, end: string | null | undefined): string {
  const a = formatDateLong(start);
  const b = formatDateLong(end);
  if (a && b) return a === b ? a : `${a} – ${b}`;
  return a || b || '';
}

export function formatDateTime(isoStr: string | null | undefined): string {
  if (!isoStr) return '—';
  const date = new Date(isoStr);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/**
 * A MARK'S TIME, in company time with the zone named.
 *
 * Separate from formatDateTime above on purpose. That one renders app chrome —
 * follow-up badges, activity rows — in the reader's own zone, which is right
 * for "when did this land in front of me". A signature caption is part of a
 * legal document: it must read the same for everyone who opens it, match the
 * PDF byte for byte, and say which zone it is in. The backend renders these the
 * same way (lib/companyTime); this is the browser's copy of one rule, not a
 * second rule.
 */
export const COMPANY_TZ = 'America/Toronto';

export function formatMarkTime(isoStr: string | null | undefined): string {
  if (!isoStr) return '—';
  const date = new Date(isoStr);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-CA', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
    timeZone: COMPANY_TZ,
    timeZoneName: 'short',
  });
}

export function daysUntil(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const date = new Date(dateStr + 'T00:00:00');
  if (isNaN(date.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((date.getTime() - today.getTime()) / 86400000);
}

export function getInitials(first: string | null | undefined, last: string | null | undefined): string {
  return ((first || '?')[0] + (last || '?')[0]).toUpperCase();
}

/**
 * Today in the user's own time zone (the browser's), as YYYY-MM-DD. Not
 * toISOString(), which is UTC: after 8 pm in Toronto it is already tomorrow,
 * and a payment entered that evening defaulted to the next day's date.
 */
export function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Compute an estimate from a lead + its tariff/branch/fees. Not yet wired into
 * the UI (estimate line items aren't persisted per lead yet), but kept ready.
 * `branchTaxRate` is passed explicitly because a lead only carries `branch_id`.
 */
export function calculateEstimate(
  lead: any,
  tariffs: any[],
  fees: any[],
  branchTaxRate = 0
): { items: { desc: string; amount: number }[]; subtotal: number; tax: number; total: number } {
  const tariff = tariffs.find((t) => t.id === lead.tariff_id);
  const items: { desc: string; amount: number }[] = [];
  let baseCharge = 0;

  if (tariff) {
    const rate = Number(tariff.base_rate ?? 0);
    if (tariff.pricing_method === 'weight') baseCharge = (Number(lead.weight_lbs) || 0) * rate;
    else if (tariff.pricing_method === 'cuft') baseCharge = (Number(lead.cubic_feet) || 0) * rate;
    else baseCharge = rate; // flat / hourly
    baseCharge = Math.max(baseCharge, Number(tariff.minimum_charge ?? 0));
    items.push({ desc: `Base rate (${tariff.name})`, amount: baseCharge });
  }

  if (Array.isArray(lead.estimate_items)) {
    for (const ei of lead.estimate_items) {
      const fee = fees.find((f) => f.id === ei.fee_id);
      if (!fee) continue;
      const amount = fee.fee_type === 'flat' ? Number(fee.amount ?? 0) : (Number(fee.percentage ?? 0) / 100) * baseCharge;
      items.push({ desc: fee.name, amount });
    }
  }

  const subtotal = items.reduce((sum, item) => sum + (item.amount || 0), 0);
  const tax = subtotal * (Number(branchTaxRate) / 100);
  return { items, subtotal, tax, total: subtotal + tax };
}
